import React, { useEffect, useMemo, useState } from 'react';
import { Drill, GenericModalContent, TagPreference } from './types/drill-types';
import { getCategoryColor, getTagColor } from '@/utils/drill-ui';
import { UnifiedTag } from './UnifiedTag';
import { DrillTagEditor } from './DrillTagEditor';
import { getYoutubeVideoId } from './services/youtube';
import { PlayIcon, EditIcon, DeleteIcon, MoreIcon, FindSimilarIcon, CopyIcon } from '@/components/shared/Icons';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { Link, Heart } from 'lucide-react';
import { DrillCardPreferences, DrillLayoutMode } from './types/drill-library-settings';
import { getDisplayedTags, isValidTagValue } from './lib/drill-card-utils';
import { getDrillDomain, getPrimaryTagValue } from '@/utils/drill-taxonomy';

interface DrillCardProps {
    drill: Drill;
    selectionMode: boolean;
    isSelected: boolean;
    onSelectionChange: (id: string, isSelected: boolean) => void;
    setGenericModalContent: (content: GenericModalContent | null) => void;
    onUpdateDrill: (drillId: string, updatedData: Partial<Drill>) => void;
    onDeleteDrill: (drillId: string) => void;
    allDrills: Drill[];
    setActiveFilters: React.Dispatch<React.SetStateAction<{ [key: string]: Set<string> }>>;
    tagPreferences: { [key: string]: TagPreference };
    activeFilters: { [key: string]: Set<string> };
    isAdminMode?: boolean;
    isFavorite?: boolean;
    onToggleFavorite?: (drillId: string) => void;
    colorizationCategory: string;
    layoutMode: DrillLayoutMode;
    cardPreferences: DrillCardPreferences;
    /** When set the card renders in pick-one mode: a prominent "Select" button replaces normal interaction. */
    onPickDrill?: (drillId: string) => void;
}
import logger from '@/lib/logging';

const DrillCard: React.FC<DrillCardProps> = ({ 
    drill, 
    selectionMode, 
    isSelected, 
    onSelectionChange, 
    setGenericModalContent, 
    onUpdateDrill, 
    onDeleteDrill, 
    allDrills, 
    setActiveFilters, 
    tagPreferences,
    activeFilters,
    isAdminMode = false,
    isFavorite = false,
    onToggleFavorite,
    colorizationCategory,
    layoutMode,
    cardPreferences,
    onPickDrill,
}) => {
    const { toast } = useToast();
    const videoId = getYoutubeVideoId(drill.videoUrl);
    const thumbnailUrl = videoId ? 
        `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : 
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjE2MCIgeT0iOTAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzlDQTNBRiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+Tm8gUHJldmlldzwvdGV4dD4KPC9zdmc+';
    
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(drill.name);
    const [editedDescription, setEditedDescription] = useState(drill.description);
    const [isEditingTags, setIsEditingTags] = useState(false);

    const [, setColorEpoch] = useState(0);
    useEffect(() => {
        void import('@/utils/drill-ui').then(({ loadColors }) =>
            loadColors().then(() => setColorEpoch((e) => e + 1))
        );
    }, []);

    const handleUpdate = () => {
        onUpdateDrill(drill.id, { name: editedName, description: editedDescription });
        setIsEditing(false);
    };

    const handleDelete = () => {
        setGenericModalContent({
            title: "Confirm Delete",
            body: (
                <div>
                    <p>Are you sure you want to delete the drill "{drill.name}"?</p>
                    <div className="flex justify-end gap-4 mt-4">
                        <Button variant="outline" onClick={() => setGenericModalContent(null)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={() => {
                            onDeleteDrill(drill.id);
                            setGenericModalContent(null);
                        }}>
                            Delete
                        </Button>
                    </div>
                </div>
            )
        });
    };
    
    const findSimilar = () => {
        const basePattern = drill.tags?.['Movement Pattern (Broad)'];
        const basePhase = drill.tags?.['Phase'];
        if (!basePattern || !basePhase) {
            setGenericModalContent({ 
                title: "Cannot Find Similar", 
                body: <p>This drill is missing 'Movement Pattern (Broad)' or 'Phase' tags.</p>
            });
            return;
        }
        const similarDrills = allDrills.filter(d => 
            d.id !== drill.id &&
            d.tags?.['Movement Pattern (Broad)'] === basePattern &&
            d.tags?.['Phase'] === basePhase
        );

        setGenericModalContent({
            title: `Similar to: ${drill.name}`,
            body: similarDrills.length > 0 ? (
                <div className="space-y-2">
                    {similarDrills.map(d => (
                        <Card key={d.id} className="p-2">
                            <p className="font-medium">{d.name}</p>
                        </Card>
                    ))}
                </div>
            ) : <p>No other drills found with pattern '{basePattern}' and phase '{basePhase}'.</p>
        });
    };

    const playVideo = () => {
        if (!videoId) return;
        setGenericModalContent({
            title: drill.name,
            padding: false,
            maxWidth: 'max-w-4xl',
            body: (
                <div className="space-y-4">
                    <div className="aspect-video">
                        <iframe 
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`} 
                            title={drill.name} 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                            allowFullScreen 
                            className="w-full h-full rounded-t-lg"
                        />
                    </div>
                    {/* Description */}
                    {drill.description && (
                        <div className="px-6">
                            <p className="text-sm text-muted">{drill.description}</p>
                        </div>
                    )}
                    {/* All Tags */}
                    <div className="px-6 pb-6">
                        <h4 className="text-sm font-semibold mb-2">Tags</h4>
                        <div className="flex flex-wrap gap-1.5">
                            {/* Domain/Main Category */}
                            {isValidTagValue(domain) && (
                                <UnifiedTag 
                                    category="Domain" 
                                    value={domain} 
                                    onClick={() => handleTagClick('Domain', domain)} 
                                    size="sm" 
                                />
                            )}
                            {/* All other tags (Domain shown above; avoid duplicate Domain chips) */}
                            {drill.tags && Object.entries(drill.tags)
                                .filter(([category]) => category !== 'Domain')
                                .map(([category, value]) => {
                                const values = Array.isArray(value) ? value.filter(isValidTagValue) : (isValidTagValue(value) ? [value] : []);
                                return values.map((val, idx) => (
                                    <UnifiedTag key={`${category}-${val}-${idx}`} category={category} value={val} onClick={() => handleTagClick(category, val)} size="sm" />
                                ));
                            })}
                        </div>
                    </div>
                </div>
            )
        });
    };

    const copyYoutubeLink = async () => {
        if (!drill.videoUrl) {
            toast({
                variant: "destructive",
                title: "No Video Link",
                description: "This drill doesn't have a YouTube video link.",
            });
            return;
        }

        try {
            // Check if Clipboard API is available
            if (!navigator.clipboard) {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = drill.videoUrl;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                try {
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    toast({
                        title: "Link Copied!",
                        description: "YouTube video link copied to clipboard.",
                    });
                } catch (err) {
                    document.body.removeChild(textArea);
                    toast({
                        variant: "destructive",
                        title: "Copy Failed",
                        description: "Could not copy link to clipboard.",
                    });
                }
                return;
            }

            // Try to write to clipboard
            await navigator.clipboard.writeText(drill.videoUrl);
            
            toast({
                title: "Link Copied!",
                description: "YouTube video link copied to clipboard.",
            });
            
        } catch (error: any) {
            // Handle permission denied or other errors
            if (error.name === 'NotAllowedError') {
                toast({
                    variant: "destructive",
                    title: "Permission Denied",
                    description: "Please grant clipboard permission in your browser settings.",
                });
            } else {
                toast({
                    variant: "destructive",
                    title: "Copy Failed",
                    description: "Could not copy link to clipboard. Please try again.",
                });
            }
            logger.error('Failed to copy YouTube link:', error);
        }
    };

    const handleTagClick = (category: string, value: string) => {
        setActiveFilters(prev => ({ ...prev, [category]: new Set([value]) }));
    };

    const { showDescription, showThumbnail, showTags, maxTagCount } = cardPreferences;
    const isRowLayout = layoutMode === 'list';
    const domain = useMemo(() => getDrillDomain(drill), [drill]);
    const primaryTags = useMemo(() => {
        if (!showTags) return [];
        return getDisplayedTags({ drill, activeFilters, maxTagCount });
    }, [drill, activeFilters, maxTagCount, showTags]);
    const shouldShowTags = showTags && primaryTags.length > 0;

    // Card border color: use the selected colorization category
    // Default to Domain if colorizationCategory is not set or if the drill doesn't have that tag
    const baseCardColor = useMemo(() => {
        if (colorizationCategory === 'Domain' || colorizationCategory === 'Category') {
            return getCategoryColor(domain, tagPreferences);
        }

        const primaryValue = getPrimaryTagValue(drill, colorizationCategory);
        if (primaryValue) {
            return getTagColor(colorizationCategory, primaryValue, tagPreferences);
        }

        return getCategoryColor(domain, tagPreferences);
    }, [colorizationCategory, domain, drill, tagPreferences]);
    const cardColor = baseCardColor;
    
    const handleCardBodyClick = () => {
        if (isEditing || isEditingTags) return;
        playVideo();
    };

    return (
        <Card
            className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] h-full flex flex-col ${
                selectionMode && isSelected ? 'ring-2 ring-accent' : ''
            }`}
        >
            {/* Pick-one mode: full-card overlay button */}
            {onPickDrill && (
                <button
                    onClick={() => onPickDrill(drill.id)}
                    className="absolute inset-0 z-30 flex items-end justify-stretch p-3 bg-transparent group"
                    aria-label={`Select ${drill.name}`}
                >
                    <span className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-accent py-2.5 text-sm font-semibold text-accent-fg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                        Select
                    </span>
                </button>
            )}
            {onToggleFavorite && (
                <div className="absolute top-2 right-2 z-20 flex items-center gap-2">
                    <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorite(drill.id);
                            }}
                            className="rounded-full bg-bg/80 p-2 backdrop-blur-sm transition-all duration-200 hover:bg-bg/90 hover:scale-110 shadow-sm"
                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <Heart 
                                className={`h-5 w-5 transition-all ${
                                    isFavorite ? 'fill-status-deviation text-status-deviation' : 'text-muted hover:text-status-deviation'
                                }`}
                            />
                        </button>
                </div>
            )}
            <div 
                className="absolute inset-0 pointer-events-none z-0 rounded-lg"
                style={{
                    background: `
                        radial-gradient(ellipse 100% 80% at 50% 0%, ${cardColor}40 0%, transparent 70%),
                        radial-gradient(ellipse 80% 100% at 0% 50%, ${cardColor}30 0%, transparent 60%)
                    `,
                    boxShadow: `
                        0 0 30px ${cardColor}50,
                        inset 0 0 30px ${cardColor}15
                    `,
                }}
            />
            <div 
                className="absolute top-0 left-0 right-0 h-2 z-10 pointer-events-none rounded-t-lg"
                style={{
                    background: `linear-gradient(90deg, ${cardColor} 0%, ${cardColor}80 40%, ${cardColor}40 70%, transparent 100%)`,
                }}
            />
            <div 
                className="absolute top-0 left-0 bottom-0 w-2 z-10 pointer-events-none rounded-l-lg"
                style={{
                    background: `linear-gradient(180deg, ${cardColor} 0%, ${cardColor}60 40%, transparent 100%)`,
                    boxShadow: `4px 0 20px ${cardColor}60, -2px 0 10px ${cardColor}40`,
                }}
            />
            
            <div
                className={
                    isRowLayout
                        ? 'relative z-10 flex h-full flex-col sm:flex-row'
                        : 'relative z-10 flex h-full flex-col'
                }
            >
                {showThumbnail && (
                    <div
                        className={`relative group cursor-pointer overflow-hidden rounded-t-lg bg-surface ${
                            isRowLayout 
                                ? 'aspect-video w-full sm:w-64 md:w-80 lg:w-96 xl:w-[28rem] sm:flex-shrink-0' 
                                : 'aspect-video'
                        }`}
                        onClick={playVideo}
                    >
                {videoId ? (
                    <LiteYouTubeEmbed id={videoId} title={drill.name} poster="maxresdefault" />
                ) : (
                            <img src={thumbnailUrl} alt={drill.name} className="h-full w-full object-cover" />
                )}
                        <div className="absolute inset-0 flex items-center justify-center bg-bg/80 opacity-0 transition-opacity group-hover:opacity-100">
                    <PlayIcon />
                </div>
                {selectionMode && (
                            <div className="absolute left-2 top-2 z-30">
                        <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => onSelectionChange(drill.id, checked as boolean)}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-bg/95 border-3 shadow-lg h-12 w-12"
                            style={{ 
                                borderWidth: '4px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
                            }}
                        />
                    </div>
                )}
            </div>
                )}

                <CardContent
                    onClick={handleCardBodyClick}
                    role="button"
                    tabIndex={0}
                    className={
                        isRowLayout
                            ? 'relative z-10 flex-1 flex flex-col justify-center min-w-0 space-y-3 sm:space-y-4 p-4 sm:p-5 md:p-6 cursor-pointer'
                            : 'relative z-10 flex-1 flex flex-col p-4 space-y-4 cursor-pointer'
                    }
                >
                {isEditing ? (
                    <div className="space-y-2">
                        <Input 
                            value={editedName} 
                                onChange={(e) => setEditedName(e.target.value)}
                            className="font-bold"
                        />
                        <Textarea 
                            value={editedDescription} 
                                onChange={(e) => setEditedDescription(e.target.value)}
                                className="min-h-[60px] text-sm"
                        />
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleUpdate}>
                                Save
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex flex-1 items-start gap-3">
                                    {selectionMode && !showThumbnail && (
                                        <Checkbox
                                            checked={isSelected}
                                            onCheckedChange={(checked) => onSelectionChange(drill.id, checked as boolean)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="mt-1 h-12 w-12 border-3 shadow-lg"
                                            style={{ 
                                                borderWidth: '4px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
                                            }}
                                        />
                                    )}
                                    <h3
                                        className={`flex-1 min-w-0 font-bold text-fg ${
                                            isRowLayout ? 'text-base sm:text-lg leading-snug' : 'text-sm leading-tight'
                                        }`}
                                    >
                                {drill.name}
                            </h3>
                                </div>
                                <div className="flex items-center gap-2">
                                    {onToggleFavorite && !showThumbnail && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleFavorite(drill.id);
                                            }}
                                            className="rounded-full bg-surface p-2 transition-colors hover:bg-surface/80"
                                            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                        >
                                            <Heart
                                                className={`h-5 w-5 transition-all ${
                                                    isFavorite
                                                        ? 'fill-status-deviation text-status-deviation'
                                                        : 'text-muted hover:text-status-deviation'
                                                }`}
                                            />
                                        </button>
                                    )}
                            {(!selectionMode || isAdminMode) && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <MoreIcon />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                copyYoutubeLink();
                                            }}
                                        >
                                            <Link className="h-4 w-4" />
                                            <span className="ml-2">Copy YouTube Link</span>
                                        </DropdownMenuItem>
                                        {isAdminMode && (
                                            <>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsEditing(true);
                                                    }}
                                                >
                                                    <EditIcon />
                                                    <span className="ml-2">Edit Name/Desc</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsEditingTags(true);
                                                    }}
                                                >
                                                    <EditIcon />
                                                    <span className="ml-2">Edit Tags</span>
                                                </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                    navigator.clipboard.writeText(JSON.stringify(drill, null, 2));
                                                    setGenericModalContent({ 
                                                                    title: 'Copied!',
                                                                    body: <p>Drill data copied to clipboard.</p>,
                                                    });
                                                            }}
                                                        >
                                                    <CopyIcon />
                                                    <span className="ml-2">Copy Drill JSON</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        findSimilar();
                                                    }}
                                                >
                                                    <FindSimilarIcon />
                                                    <span className="ml-2">Find Similar</span>
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete();
                                                    }}
                                                    className="text-danger"
                                                >
                                                    <DeleteIcon />
                                                    <span className="ml-2">Delete Drill</span>
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                            </div>
                            {showDescription && drill.description && (
                                <p
                                    className={`text-muted min-w-0 ${
                                        isRowLayout 
                                            ? 'text-sm sm:text-base leading-relaxed line-clamp-3' 
                                            : 'text-sm mb-1 line-clamp-2'
                                    }`}
                                >
                            {drill.description}
                        </p>
                            )}
                    </>
                )}
                
                    {shouldShowTags && (
                        <div className="flex flex-wrap gap-1.5 mt-auto">
                            {primaryTags.map(({ category, value }, idx) => (
                                <UnifiedTag
                                    key={`${category}::${value}::${idx}`}
                                    category={category}
                                    value={value}
                                    onClick={(() => {
                                        handleTagClick(category, value);
                                    }) as () => void}
                                    size="sm"
                                    isActive={!!activeFilters[category]?.has(value)}
                                />
                            ))}
                </div>
                    )}
            </CardContent>
            </div>

            <DrillTagEditor
                drill={drill}
                isOpen={isEditingTags}
                onClose={() => setIsEditingTags(false)}
                onSave={(drillId, updatedTags) => {
                    onUpdateDrill(drillId, { tags: updatedTags });
                    setIsEditingTags(false);
                }}
            />
        </Card>
    );
};

export default DrillCard;
